import { Tab } from '@headlessui/react';
import { type FC, type ReactNode, createContext, Fragment } from 'react';

import { cn } from '../utils/cn';

export interface TabsProps {
  /**
   * Default selected tab index
   * @default 0
   */
  defaultIndex?: number;

  /**
   * Controlled selected index
   */
  selectedIndex?: number;

  /**
   * Callback when tab changes
   */
  onChange?: (index: number) => void;

  /**
   * Tabs variant
   * @default 'default'
   */
  variant?: 'default' | 'pills' | 'underline';

  /**
   * Children (TabList and TabPanels)
   */
  children: ReactNode;

  /**
   * Custom class for the container
   */
  className?: string;
}

// Create context outside component to prevent recreation on every render
const TabsContext = createContext<{ variant: string }>({ variant: 'default' });

/**
 * Tabs component
 *
 * Accessible tabs component built with Headless UI.
 * Supports multiple variants and keyboard navigation.
 *
 * @example
 * ```tsx
 * <Tabs>
 *   <TabList>
 *     <TabItem>Tab 1</TabItem>
 *     <TabItem>Tab 2</TabItem>
 *   </TabList>
 *   <TabPanels>
 *     <TabPanel>Content 1</TabPanel>
 *     <TabPanel>Content 2</TabPanel>
 *   </TabPanels>
 * </Tabs>
 * ```
 */
export const Tabs: FC<TabsProps> = ({
  defaultIndex = 0,
  selectedIndex,
  onChange,
  variant = 'default',
  className,
  children,
}) => {
  return (
    <TabsContext.Provider value={{ variant }}>
      <Tab.Group defaultIndex={defaultIndex} selectedIndex={selectedIndex} onChange={onChange}>
        <div className={className}>{children}</div>
      </Tab.Group>
    </TabsContext.Provider>
  );
};

Tabs.displayName = 'Tabs';

export interface TabListProps {
  /**
   * Children (TabItem components)
   */
  children: ReactNode;

  /**
   * Custom class for the tab list
   */
  className?: string;
}

/**
 * TabList component
 *
 * Container for tab buttons.
 */
export const TabList: FC<TabListProps> = ({ className, children }) => {
  return (
    <Tab.List className={cn('flex gap-1 border-b border-gray-200 dark:border-gray-700', className)}>
      {children}
    </Tab.List>
  );
};

TabList.displayName = 'TabList';

export interface TabItemProps {
  /**
   * Tab content
   */
  children: ReactNode;

  /**
   * Custom class for the tab
   */
  className?: string;

  /**
   * Disabled state
   */
  disabled?: boolean;
}

/**
 * TabItem component
 *
 * Individual tab button.
 */
export const TabItem: FC<TabItemProps> = ({ className, children, disabled = false }) => {
  return (
    <Tab as={Fragment}>
      {({ selected }) => (
        <button
          className={cn(
            'px-4 py-2 text-sm font-medium transition-colors duration-200',
            'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            selected
              ? 'border-b-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600',
            className
          )}
          disabled={disabled}
        >
          {children}
        </button>
      )}
    </Tab>
  );
};

TabItem.displayName = 'TabItem';

export interface TabPanelsProps {
  /**
   * Children (TabPanel components)
   */
  children: ReactNode;

  /**
   * Custom class for the panels container
   */
  className?: string;
}

/**
 * TabPanels component
 *
 * Container for tab panel content.
 */
export const TabPanels: FC<TabPanelsProps> = ({ className, children }) => {
  return <Tab.Panels className={className}>{children}</Tab.Panels>;
};

TabPanels.displayName = 'TabPanels';

export interface TabPanelProps {
  /**
   * Panel content
   */
  children: ReactNode;

  /**
   * Custom class for the panel
   */
  className?: string;
}

/**
 * TabPanel component
 *
 * Individual tab panel content.
 */
export const TabPanel: FC<TabPanelProps> = ({ className, children }) => {
  return <Tab.Panel className={cn('py-4 focus:outline-none', className)}>{children}</Tab.Panel>;
};

TabPanel.displayName = 'TabPanel';
